import { useState, useEffect } from "react";

const SERVICE_TYPES = ["ride", "delivery", "hauling"];

export default function Pricing({ supabase }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchingFuel, setFetchingFuel] = useState(false);
  const [activeTab, setActiveTab] = useState("ride");
  const [form, setForm] = useState({});
  const [fuelNote, setFuelNote] = useState("");

  useEffect(() => { loadConfigs(); }, []);
  useEffect(() => { if (configs.length > 0) loadForm(activeTab); }, [activeTab, configs]);

  async function loadConfigs() {
    const { data } = await supabase.from("pricing_config").select("*").order("service_type");
    if (data) setConfigs(data);
    setLoading(false);
  }

  function loadForm(type) {
    const config = configs.find(c => c.service_type === type);
    if (config) setForm({ ...config });
    else setForm({ service_type: type, base_rate: 2.85, long_distance_rate: 3.50, convenience_fee: 4, flat_rate_under_km: 2, flat_rate_amount: 12, fuel_price_per_liter: 0, fuel_auto_adjust: false, base_fuel_price: 0 });
  }

  async function handleSave() {
    setSaving(true);
    const existing = configs.find(c => c.service_type === activeTab);
    if (existing) {
      await supabase.from("pricing_config").update({ ...form, updated_at: new Date().toISOString() }).eq("id", existing.id);
    } else {
      await supabase.from("pricing_config").insert({ ...form });
    }
    await loadConfigs();
    setSaving(false);
    alert("Pricing updated successfully!");
  }

  async function fetchCurrentFuelPrice() {
    setFetchingFuel(true);
    setFuelNote("");
    try {
      // Search for current Sierra Leone fuel price via web
      const res = await fetch(
        "https://api.globalpetrolprices.com/v1/prices?country=SL&fuel=gasoline",
        { headers: { "Accept": "application/json" } }
      );
      if (res.ok) {
        const data = await res.json();
        const price = data?.price;
        if (price) {
          setForm(f => ({ ...f, fuel_price_per_liter: parseFloat(price.toFixed(2)) }));
          setFuelNote(`✅ Fetched: Le ${price}/L — Auto-calculate rates below`);
          setFetchingFuel(false);
          return;
        }
      }
    } catch (e) {}

    // Fallback: use known SL average (update manually)
    try {
      const res2 = await fetch("https://www.globalpetrolprices.com/Sierra-Leone/gasoline_prices/", {
        headers: { "User-Agent": "Mozilla/5.0" }
      });
      const text = await res2.text();
      const match = text.match(/(\d+\.\d+)\s*SLL/i);
      if (match) {
        const sllPrice = parseFloat(match[1]);
        const slePrice = sllPrice / 1000;
        setForm(f => ({ ...f, fuel_price_per_liter: parseFloat(slePrice.toFixed(2)) }));
        setFuelNote(`✅ Estimated SL fuel price: Le ${slePrice.toFixed(2)}/L`);
        setFetchingFuel(false);
        return;
      }
    } catch (e) {}

    // Final fallback — manual entry prompt
    setFuelNote("⚠️ Could not auto-fetch price. Please enter the current fuel price manually (check NP Sierra Leone).");
    setFetchingFuel(false);
  }

  function autoCalculateRates() {
    const fuel = parseFloat(form.fuel_price_per_liter);
    const baseFuel = parseFloat(form.base_fuel_price);
    if (!fuel || !baseFuel) { alert("Please set both current fuel price and base fuel price first."); return; }
    const multiplier = fuel / baseFuel;
    setForm(f => ({
      ...f,
      base_rate: parseFloat((f.base_rate * multiplier).toFixed(2)),
      long_distance_rate: parseFloat((f.long_distance_rate * multiplier).toFixed(2)),
      flat_rate_amount: parseFloat((f.flat_rate_amount * multiplier).toFixed(2)),
    }));
    setFuelNote(`✅ Rates adjusted by ${((multiplier - 1) * 100).toFixed(1)}% based on fuel price change.`);
  }

  const inputStyle = { background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, padding: "10px 14px", color: "#fff", fontSize: 14, width: "100%", boxSizing: "border-box" };
  const labelStyle = { color: "#666", fontSize: 12, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 };

  function Field({ label, field, type = "number", step = "0.01" }) {
    return (
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>{label}</label>
        <input type={type} step={step} style={inputStyle} value={form[field] ?? ""} onChange={e => setForm(f => ({ ...f, [field]: type === "number" ? parseFloat(e.target.value) || 0 : e.target.value })) } />
      </div>
    );
  }

  // Preview fare calculation
  function previewFare(km) {
    if (!form.base_rate) return "—";
    if (km < (form.flat_rate_under_km || 2)) return `Le ${form.flat_rate_amount}`;
    let dist = km <= 10 ? form.base_rate * km : (form.base_rate * 10) + (form.long_distance_rate * (km - 10));
    return `Le ${(Math.ceil((dist + form.convenience_fee) * 2) / 2).toFixed(2)}`;
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {SERVICE_TYPES.map(t => (
          <button key={t} style={{ background: activeTab === t ? "#00A651" : "#111", border: activeTab === t ? "1px solid #00A651" : "1px solid #333", borderRadius: 20, padding: "8px 20px", color: activeTab === t ? "#fff" : "#666", cursor: "pointer", fontSize: 14, fontWeight: "600", textTransform: "capitalize" }} onClick={() => setActiveTab(t)}>
            {t === "ride" ? "🚗 Rides" : t === "delivery" ? "🚴 Delivery" : "📦 Hauling"}
          </button>
        ))}
      </div>

      {loading ? <div style={{ color: "#666", padding: 40, textAlign: "center" }}>Loading pricing...</div> : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>

          {/* Left: Rate Config */}
          <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 24 }}>
            <h3 style={{ color: "#fff", marginTop: 0, marginBottom: 20 }}>Rate Configuration — {activeTab}</h3>
            <Field label="Base Rate (Le/km)" field="base_rate" />
            <Field label="Long Distance Rate (Le/km, over 10km)" field="long_distance_rate" />
            <Field label="Convenience Fee (Le)" field="convenience_fee" />
            <Field label="Flat Rate Under (km)" field="flat_rate_under_km" />
            <Field label="Flat Rate Amount (Le)" field="flat_rate_amount" />
            <button style={{ background: "#00A651", border: "none", borderRadius: 8, padding: "12px 24px", color: "#fff", fontWeight: "bold", fontSize: 14, cursor: "pointer", width: "100%", marginTop: 8 }} onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "💾 Save Rates"}
            </button>
          </div>

          {/* Right: Fuel Adjustment */}
          <div>
            <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 24, marginBottom: 16 }}>
              <h3 style={{ color: "#fff", marginTop: 0, marginBottom: 6 }}>⛽ Fuel Price Adjustment</h3>
              <p style={{ color: "#666", fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
                Set the current SL fuel price to automatically adjust all rates proportionally. Use "Base Fuel Price" as the reference price when rates were originally set.
              </p>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Current Fuel Price (Le/liter)</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input type="number" step="0.01" style={{ ...inputStyle, flex: 1 }} value={form.fuel_price_per_liter ?? 0} onChange={e => setForm(f => ({ ...f, fuel_price_per_liter: parseFloat(e.target.value) || 0 }))} />
                  <button style={{ background: "#0057A820", border: "1px solid #0057A8", borderRadius: 8, padding: "10px 14px", color: "#0057A8", fontWeight: "600", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }} onClick={fetchCurrentFuelPrice} disabled={fetchingFuel}>
                    {fetchingFuel ? "Fetching..." : "🌐 Auto-Fetch"}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Base Fuel Price (Le/liter) — when rates were set</label>
                <input type="number" step="0.01" style={inputStyle} value={form.base_fuel_price ?? 0} onChange={e => setForm(f => ({ ...f, base_fuel_price: parseFloat(e.target.value) || 0 }))} />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, background: "#1a1a1a", padding: 12, borderRadius: 8 }}>
                <input type="checkbox" id="autoAdj" checked={form.fuel_auto_adjust || false} onChange={e => setForm(f => ({ ...f, fuel_auto_adjust: e.target.checked }))} style={{ width: 16, height: 16, cursor: "pointer" }} />
                <label htmlFor="autoAdj" style={{ color: "#ccc", fontSize: 14, cursor: "pointer" }}>Auto-adjust rates when fuel price is updated</label>
              </div>

              {fuelNote && (
                <div style={{ background: fuelNote.startsWith("✅") ? "#00A65115" : "#f39c1215", border: `1px solid ${fuelNote.startsWith("✅") ? "#00A65140" : "#f39c1240"}`, borderRadius: 8, padding: 10, color: fuelNote.startsWith("✅") ? "#00A651" : "#f39c12", fontSize: 13, marginBottom: 16 }}>
                  {fuelNote}
                </div>
              )}

              <button style={{ background: "#f39c1220", border: "1px solid #f39c12", borderRadius: 8, padding: "12px", color: "#f39c12", fontWeight: "600", fontSize: 14, cursor: "pointer", width: "100%" }} onClick={autoCalculateRates}>
                ⚡ Auto-Calculate Rates from Fuel Price
              </button>
            </div>

            {/* Fare Preview */}
            <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 24 }}>
              <h3 style={{ color: "#fff", marginTop: 0, marginBottom: 16 }}>📊 Fare Preview</h3>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Distance", "Estimated Fare"].map(h => (
                      <th key={h} style={{ color: "#666", fontSize: 12, textAlign: "left", padding: "8px 0", borderBottom: "1px solid #222", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 5, 10, 15, 20].map(km => (
                    <tr key={km} style={{ borderBottom: "1px solid #1a1a1a" }}>
                      <td style={{ color: "#ccc", padding: "10px 0", fontSize: 14 }}>{km} km</td>
                      <td style={{ color: "#00A651", padding: "10px 0", fontSize: 14, fontWeight: "600" }}>{previewFare(km)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
